'use client';

import {
  Box,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { BatchMode, ErrorPolicy } from '@repo/api-gw-sdk';

import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';

import type { IngestionPipelineWizardState } from '../IngestionPipelineWizardState';
import { getStepperLabels } from '../kafkaHelpers';

const BATCH_MODE_OPTIONS = [
  { value: BatchMode.BATCH_MODE_STREAMING, label: 'Streaming' },
  { value: BatchMode.BATCH_MODE_MICRO_BATCH, label: 'Micro Batch' },
];

const ERROR_POLICY_OPTIONS = [
  { value: ErrorPolicy.ERROR_POLICY_FAIL, label: 'Fail' },
  { value: ErrorPolicy.ERROR_POLICY_SKIP, label: 'Skip' },
];

export function KafkaSettingsStep(
  props: StepProps<IngestionPipelineWizardState>
) {
  const {
    kafkaPartitioningColumns,
    kafkaBatchMode,
    kafkaMaxRecordsPerBatch,
    kafkaMaxBatchIntervalSeconds,
    kafkaErrorPolicy,
    kafkaMaxRetries,
  } = props.wizardState;

  const canGoNext = () =>
    kafkaPartitioningColumns.length > 0 &&
    kafkaPartitioningColumns.every((col) => col.trim() !== '');

  const handleColumnChange = (index: number, value: string) => {
    props.setWizardState((s) => {
      const updated = [...s.kafkaPartitioningColumns];
      updated[index] = value;
      return { ...s, kafkaPartitioningColumns: updated };
    });
  };

  const handleAddColumn = () => {
    props.setWizardState((s) => ({
      ...s,
      kafkaPartitioningColumns: [...s.kafkaPartitioningColumns, ''],
    }));
  };

  const handleRemoveColumn = (index: number) => {
    props.setWizardState((s) => {
      const updated = s.kafkaPartitioningColumns.filter((_, i) => i !== index);
      return {
        ...s,
        kafkaPartitioningColumns: updated.length > 0 ? updated : [''],
      };
    });
  };

  return (
    <StepContainer
      stepperIndex={4}
      stepperLabels={getStepperLabels(props.wizardState.sourceType)}
      canGoNext={canGoNext}
      onNextClick={async () => {
        props.setNextStep(props.currentStep.next?.[0]);
      }}
      onBackClick={props.back}
      sx={{ overflow: 'auto', paddingX: '0px', paddingY: 'min(60px, 4vw)' }}
    >
      <Stack gap='min(60px, 4vw)' divider={<Divider />}>
        {/* Partitioning */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Partitioning</Typography>
              <Typography variant='body2' color='text.secondary'>
                Specify the source columns used to partition ingested data.
              </Typography>
            </Stack>

            <Stack gap='12px'>
              {kafkaPartitioningColumns.map((col, index) => (
                <Stack
                  key={index}
                  direction='row'
                  gap='8px'
                  alignItems='center'
                >
                  <TextField
                    size='small'
                    fullWidth
                    placeholder='e.g. event_date'
                    value={col}
                    onChange={(e) => handleColumnChange(index, e.target.value)}
                    slotProps={{
                      htmlInput: {
                        'data-testid': `kafka-partitioning-column-${index}`,
                      },
                    }}
                  />
                  {kafkaPartitioningColumns.length > 1 && (
                    <IconButton
                      size='small'
                      onClick={() => handleRemoveColumn(index)}
                      data-testid={`kafka-partitioning-column-remove-${index}`}
                    >
                      <i
                        className='material-symbols-close-rounded'
                        style={{ fontSize: '18px' }}
                      />
                    </IconButton>
                  )}
                </Stack>
              ))}
              <IconButton
                size='small'
                onClick={handleAddColumn}
                data-testid='kafka-partitioning-add-column'
              >
                <i
                  className='material-symbols-add-rounded'
                  style={{ fontSize: '18px' }}
                />
              </IconButton>
            </Stack>
          </Stack>
        </Box>

        {/* Batch Processing */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Batch Processing</Typography>
              <Typography variant='body2' color='text.secondary'>
                Configure how messages are batched before writing to the table.
              </Typography>
            </Stack>

            <FormControl fullWidth>
              <FormLabel>Mode</FormLabel>
              <Select
                size='small'
                value={kafkaBatchMode}
                onChange={(e) => {
                  const val = Object.values(BatchMode).find(
                    (v) => v === e.target.value
                  );
                  if (val) {
                    props.setWizardState((s) => ({
                      ...s,
                      kafkaBatchMode: val,
                    }));
                  }
                }}
                data-testid='kafka-batch-mode-select'
              >
                {BATCH_MODE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <FormLabel>Max Records per Batch</FormLabel>
              <TextField
                size='small'
                type='number'
                placeholder='e.g. 10000'
                value={kafkaMaxRecordsPerBatch}
                onChange={(e) =>
                  props.setWizardState((s) => ({
                    ...s,
                    kafkaMaxRecordsPerBatch: e.target.value,
                  }))
                }
                slotProps={{
                  htmlInput: {
                    'data-testid': 'kafka-max-records-per-batch-input',
                  },
                }}
              />
            </FormControl>

            <FormControl fullWidth>
              <FormLabel>Max Batch Interval (seconds)</FormLabel>
              <TextField
                size='small'
                type='number'
                placeholder='e.g. 60'
                value={kafkaMaxBatchIntervalSeconds}
                onChange={(e) =>
                  props.setWizardState((s) => ({
                    ...s,
                    kafkaMaxBatchIntervalSeconds: e.target.value,
                  }))
                }
                slotProps={{
                  htmlInput: {
                    'data-testid': 'kafka-max-batch-interval-seconds-input',
                  },
                }}
              />
            </FormControl>
          </Stack>
        </Box>

        {/* Error Handling */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Error Handling</Typography>
              <Typography variant='body2' color='text.secondary'>
                Define how the pipeline handles failed messages.
              </Typography>
            </Stack>

            <FormControl fullWidth>
              <FormLabel>Error Policy</FormLabel>
              <Select
                size='small'
                value={kafkaErrorPolicy}
                onChange={(e) => {
                  const val = Object.values(ErrorPolicy).find(
                    (v) => v === e.target.value
                  );
                  if (val) {
                    props.setWizardState((s) => ({
                      ...s,
                      kafkaErrorPolicy: val,
                    }));
                  }
                }}
                data-testid='kafka-error-policy-select'
              >
                {ERROR_POLICY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <FormLabel>Max Retries</FormLabel>
              <TextField
                size='small'
                type='number'
                placeholder='3'
                value={kafkaMaxRetries}
                onChange={(e) =>
                  props.setWizardState((s) => ({
                    ...s,
                    kafkaMaxRetries: e.target.value,
                  }))
                }
                slotProps={{
                  htmlInput: { 'data-testid': 'kafka-max-retries-input' },
                }}
              />
            </FormControl>
          </Stack>
        </Box>
      </Stack>
    </StepContainer>
  );
}
